import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { ApiHandler } from "../../helper/ApiHandler";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from '../../components/Breadcrumb';
import Container from '../../components/Container';
import { UploadImage } from "../../helper/UploadFile";
import Loading from "../../components/Loading";

const AddBikes = () => {
    const location = useLocation();
    const token = useSelector((state) => state.user.userToken)
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const initialState = {
        title: "",
        model: "",
        current_price: "",
        top_speed: "",
        year: "",
        category: "",
        brand: "",
        fuel_type: "1",
    }

    const [formData, setFormData] = useState(initialState);
    const [categories, setCategories] = useState([]);
    const [fuelTypes, setFuelTypes] = useState([]);
    const [brands, setBrands] = useState([]);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [profile_image, setProfile_image] = useState("");
    const [priceRanges, setPriceRanges] = useState([]);
    const [budget, setBudget] = useState({ budget_start_amount: "", budget_end_amount: "" });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await ApiHandler("/get_bikes_section.php", "GET", undefined, token, dispatch, navigate);
                setCategories(response.data.data.category || []);
                setFuelTypes(response.data.data.fuel_type || []);
                setBrands(response.data.data.brands || []);
                setPriceRanges(response.data.data.bike_budget || []);
            } catch (error) {
                console.error("Error fetching dropdown options:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchOptions();
    }, []);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            setProfile_image(imageUrl);
            setUploadedImage(file); // Store file for API upload
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Handle Dropdown Change
    const handleSelectChange = (e) => {
        const [start, end] = e.target.value.split("-");
        setBudget({ budget_start_amount: start, budget_end_amount: end });
    };

    const [galleryImages, setGalleryImages] = useState([]); // Stores uploaded images (URLs)

    // Handle gallery image upload
    const handleGalleryUpload = async (e) => {
        if (galleryImages.length >= 20) {
            toast.error("You can upload a maximum of 20 images.");
            return;
        }

        const file = e.target.files[0];
        if (file) {
            const uploadResponse = await UploadImage(file, token, navigate);
            if (uploadResponse) {
                setGalleryImages((prev) => [...prev, `https://tezbikes.com/tezbike/assets/${uploadResponse}`]);
            }
        }
    };

    // Remove an image from the gallery
    const handleRemoveGalleryImage = (index) => {
        setGalleryImages((prev) => prev.filter((_, i) => i !== index));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        let uploadedImageUrl = null;

        // Ensure image is uploaded
        if (!uploadedImage) {
            toast.error('Please upload an image before submitting');
            return;
        }

        // Ensure feature image is uploaded
        if (galleryImages.length === 0) {
            toast.error('Please upload a gallery Images before submitting');
            setLoading(false);
            return;
        }

        if (uploadedImage) {
            const uploadResponse = await UploadImage(uploadedImage, token, navigate);
            setProfile_image(`https://tezbikes.com/tezbike/assets/${uploadResponse}`);
            uploadedImageUrl = uploadResponse;
        }

        // Extract filenames from gallery image URLs
        const galleryFilenames = galleryImages.map(img => img.split('/').pop());

        const body = {
            title: formData.title,
            model: formData.model,
            current_price: formData.current_price,
            top_speed: formData.top_speed,
            year: formData.year,
            category_id: formData.category,
            brand_id: formData.brand,
            fuel_type_id: formData.fuel_type,
            budget_start_amount: budget.budget_start_amount,
            budget_end_amount: budget.budget_end_amount,
            image: uploadedImageUrl,
            multiple_image: galleryFilenames, // Gallery Images Array
        }

        try {
            const response = await ApiHandler(
                "/add_bikes.php",
                "POST",
                body,
                token,
                dispatch,
                navigate
            );

            if (response) {
                toast.success(response?.data.msg)
                setFormData(initialState);
                setProfile_image("");
                setUploadedImage(null);
                navigate(`/admin/bikes/history`);
            }
        } catch (error) {
            toast.error(error)
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loading />
    return (
        <>
            <Breadcrumb pageName="Add Bikes" />
            <Container>
                <>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
                        {/* Input Fields */}
                        {[
                            { label: "Bike Title", name: "title", type: "text" },
                            { label: "Bike Name", name: "model", type: "model" },
                            { label: "Current Price", name: "current_price", type: "number" },
                            { label: "Kilometer (KMH)", name: "top_speed", type: "number" },
                            { label: "Modal Year", name: "year", type: "number" },
                        ].map(({ label, name, type, fullWidth }) => (
                            <div key={name} className={fullWidth ? "sm:col-span-2" : ""}>
                                <label className="block text-sm font-bold text-gray-700">{label}</label>
                                <input type={type} name={name} required value={formData[name]} onChange={handleChange} className="w-full border p-2 rounded" />
                            </div>
                        ))}

                        {/* Dropdown Select Fields */}
                        {[
                            { label: "Category", name: "category", options: categories, optionKey: "category_name" },
                            { label: "Brand", name: "brand", options: brands, optionKey: "brands_name" },
                            { label: "Fuel Type", name: "fuel_type", options: fuelTypes, optionKey: "fuel_type" },
                        ].map(({ label, name, options, optionKey }) => (
                            <div key={name}>
                                <label className="block text-sm font-bold text-gray-700">{label}</label>
                                <select
                                    name={name}
                                    required
                                    value={formData[name]}
                                    onChange={handleChange}
                                    className="w-full border p-2 rounded focus:ring focus:ring-blue-200"
                                >
                                    <option value="">Select {label}</option>
                                    {options.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option[optionKey]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ))}

                        <div>
                            <label className="block text-sm font-bold text-gray-700">Price Range</label>
                            <select
                                required
                                className="w-full border p-2 rounded focus:ring"
                                value={`${budget.budget_start_amount}-${budget.budget_end_amount}`}
                                onChange={handleSelectChange}>
                                <option value="">Select a price range</option>
                                {priceRanges.map((range) => (
                                    <option key={range.id} value={`${range.start_amount}-${range.end_amount}`}>
                                        {range.start_amount} - {range.end_amount}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Category Image Upload */}
                        <div className="col-span-2">
                            <label htmlFor="category_image" className="block text-[#222] font-semibold mb-1">
                                Image (Required)
                            </label>
                            {profile_image && (
                                <div className="mt-2">
                                    <img src={profile_image} alt="Preview" className="w-28 h-28 rounded-md border object-contain" />
                                </div>
                            )}
                            <input
                                type="file"
                                name="category_image"
                                accept="image/*"
                                onChange={handleImageUpload}
                                required
                                className="border rounded-md p-2 w-full"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-[#222] font-semibold mb-1">
                                Gallery Images (Min 1 - Max 20)
                            </label>
                            <div className="flex flex-wrap gap-3 mt-2">
                                {/* Show Uploaded Images */}
                                {galleryImages.map((img, index) => (
                                    <div key={index} className="relative w-28 h-28">
                                        <img src={img} alt={`Gallery ${index}`} className="w-full h-full object-cover rounded-md border" />
                                        {/* Remove Button */}
                                        <button
                                            onClick={() => handleRemoveGalleryImage(index)}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 text-sm"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}

                                {/* Add Image Button */}
                                {galleryImages.length < 20 && (
                                    <label className="w-28 h-28 flex items-center justify-center border rounded-md cursor-pointer hover:bg-gray-100">
                                        <span className="text-gray-500 text-2xl">+</span>
                                        <input type="file" className="hidden" accept="image/*" onChange={handleGalleryUpload} />
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button type="submit" className="col-span-1 sm:col-span-2 bg-blue-500 text-white py-2 rounded">
                            {loading ? "Submitting..." : "Submit"}
                        </button>
                    </form>
                </>
            </Container>
        </>
    );
};

export default AddBikes;
